import { useId, useState, type ReactNode } from "react";
import { Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AttachmentGrid, AttachmentTile } from "@/components/AttachmentTile";
import { BusyIndicator } from "@/components/BusyIndicator";
import {
  appendMarkdown,
  RichTextEditor,
  type InlineImage,
  type InlineImagePreviewer,
  type InlineImageUploader,
} from "@/components/RichTextEditor";
import { deflateInlineImages } from "@/utils/inlineImages";
import { MESSAGE_BODY_MAX } from "@/features/conversations/api/conversations.api";
import { MESSAGE_TOO_LONG_ERROR } from "@/features/conversations/messageCopy";
import { ATTACHMENT_ACCEPT } from "@/features/conversations/attachments";
import { useAttachmentDraft } from "@/features/conversations/useAttachmentDraft";

export interface ComposerSubmit {
  body: string;
  files: File[];
  isInternal: boolean;
}

const MESSAGE_REQUIRED_ERROR = "A message is required.";

export function MessageComposer({
  placeholder,
  submitLabel,
  busyLabel,
  rows = 4,
  autoFocus = false,
  internalLocked = false,
  internalLockedHint,
  showInternalToggle = true,
  insertControl,
  uploadImage,
  previewImage,
  onCancel,
  onSubmit,
}: {
  placeholder: string;
  submitLabel: string;
  busyLabel: string;
  rows?: number;
  autoFocus?: boolean;
  internalLocked?: boolean;
  internalLockedHint?: string;
  showInternalToggle?: boolean;
  insertControl?: (append: (content: string) => void) => ReactNode;
  uploadImage?: InlineImageUploader;
  previewImage?: InlineImagePreviewer;
  onCancel?: () => void;
  onSubmit: (payload: ComposerSubmit) => Promise<string | null>;
}) {
  const fieldId = useId();
  const draft = useAttachmentDraft();

  const [body, setBody] = useState("");
  const [images, setImages] = useState<InlineImage[]>([]);
  const [templateInsertedAt, setTemplateInsertedAt] = useState(0);
  const [internal, setInternal] = useState(false);
  const [bodyError, setBodyError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const files = draft.files;

  async function upload(file: File): Promise<InlineImage> {
    if (!uploadImage) throw new Error("Images are not supported here.");
    const image = await uploadImage(file);
    setImages((prev) => [...prev, image]);
    return image;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    const trimmed = deflateInlineImages(body, new Map(images.map((i) => [i.url, i.id]))).trim();
    if (trimmed.length === 0) {
      setBodyError(MESSAGE_REQUIRED_ERROR);
      return;
    }
    if (trimmed.length > MESSAGE_BODY_MAX) {
      setBodyError(MESSAGE_TOO_LONG_ERROR);
      return;
    }
    setBodyError(null);
    setSubmitError(null);
    setBusy(true);
    try {
      const failure = await onSubmit({
        body: trimmed,
        files,
        isInternal: internalLocked || internal,
      });
      if (failure) {
        setSubmitError(failure);
        return;
      }
      setBody("");
      setImages([]);
      draft.reset();
      setInternal(false);
      setBodyError(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-white p-3">
      {insertControl && (
        <div className="mb-2">
          {insertControl((content) => {
            setBody((prev) => appendMarkdown(prev, content));
            setBodyError(null);
            setTemplateInsertedAt((n) => n + 1);
          })}
        </div>
      )}

      <RichTextEditor
        autoFocus={autoFocus}
        revealEndSignal={templateInsertedAt}
        value={body}
        onChange={(markdown) => {
          setBody(markdown);
          if (bodyError) setBodyError(null);
        }}
        invalid={bodyError != null}
        compact={rows <= 2}
        placeholder={placeholder}
        uploadImage={uploadImage ? upload : undefined}
        previewImage={previewImage}
      />

      {bodyError && <p className="mt-2 text-xs text-red-600">{bodyError}</p>}

      {files.length > 0 && (
        <div className="mt-3 space-y-3">
          {busy && (
            <div className="rounded-md border border-primary bg-primary/10 px-4 py-3">
              <BusyIndicator label="Uploading…" />
            </div>
          )}
          <AttachmentGrid>
            {files.map((file, index) => (
              <AttachmentTile
                key={`${file.name}-${index}`}
                filename={file.name}
                contentType={file.type}
                sizeBytes={file.size}
                disabled={busy}
                onRemove={() => draft.removeFile(index)}
              />
            ))}
          </AttachmentGrid>
        </div>
      )}

      {draft.error && <p className="mt-2 text-xs text-red-600">{draft.error}</p>}

      <input
        ref={draft.inputRef}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        className="hidden"
        onChange={(e) => draft.addFiles(e.target.files)}
      />

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={draft.openFilePicker}
            className="flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline"
          >
            <Paperclip className="h-4 w-4" />
            Attach
          </button>

          {showInternalToggle && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={fieldId}
                checked={internalLocked || internal}
                disabled={internalLocked}
                onCheckedChange={(v) => {
                  setInternal(v === true);
                  setSubmitError(null);
                }}
              />
              {internalLocked && internalLockedHint ? (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Label
                        htmlFor={fieldId}
                        className="text-sm font-normal text-slate-600 underline decoration-dotted underline-offset-2"
                      >
                        Internal note
                      </Label>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-56">{internalLockedHint}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Label htmlFor={fieldId} className="text-sm font-normal text-slate-600">
                  Internal note
                </Label>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {onCancel && (
            <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" size="sm" disabled={busy}>
            {busy && <Spinner />}
            {busy ? busyLabel : submitLabel}
          </Button>
        </div>
      </div>

      {submitError && (
        <p role="alert" className="mt-3 text-xs text-red-600">
          {submitError}
        </p>
      )}
    </form>
  );
}
